/**
 * Matches agent usernames like "dmd100001" or "DMD100001".
 * Anchored (^...$) so it only matches when the entire string is a username,
 * leaving emails, notes, or other text that merely contains one untouched.
 */
const AGENT_USERNAME = /^dmd\d{6}$/i;

/**
 * True for plain data objects: object literals, parsed JSON, and
 * Object.create(null) objects. False for arrays, Dates, Maps, class instances,
 * etc., so we don't rebuild objects whose prototype/behavior we'd lose.
 */
const isPlainObject = (v: unknown): v is Record<string, unknown> => {
  if (typeof v !== 'object' || v === null) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};

/**
 * Recursively walks a value and uppercases any string that is an agent
 * username (e.g. "dmd100001" -> "DMD100001").
 *
 * - Pure: returns a new structure and never mutates the input, so it's safe
 *   to use with React state, context, and React Query `select`.
 * - Matches on string values, not key names, so it catches usernames in any
 *   field (agentUsername, uplineId, createdBy, ...) at any depth.
 * - Object keys are not transformed. A map keyed by username keeps its
 *   original keys.
 * - The return type matches the input type, so callers keep full typing.
 */
export function normalizeAgentUsernames<T>(value: T): T {
  // Leaf case: uppercase only if the whole string is an agent username.
  if (typeof value === 'string') {
    return (AGENT_USERNAME.test(value) ? value.toUpperCase() : value) as T;
  }

  // Arrays: normalize each element, preserving order.
  if (Array.isArray(value)) {
    return value.map(normalizeAgentUsernames) as T;
  }

  // Plain objects: rebuild with the same keys and normalized values.
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, normalizeAgentUsernames(v)]),
    ) as T;
  }

  // Everything else (numbers, booleans, null, undefined, Dates, class
  // instances) is returned as-is.
  return value;
}
